import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useHistory } from 'react-router-dom';
import { connect } from 'react-redux';

import { isScrollingDisabled } from '../../ducks/ui.duck';

import { Page, LayoutSingleColumn } from '../../components';
import TopbarContainer from '../../containers/TopbarContainer/TopbarContainer';
import FooterContainer from '../../containers/FooterContainer/FooterContainer';

import { companyInfo, guidelineSections } from './communityGuidelinesContent';

import css from './CommunityGuidelinesPage.module.css';

import { ReactComponent as DownloadIcon } from '../../assets/svg/downloadIcon.svg';

const Paragraph = ({ id }) => (
  <p className={css.paragraph}>
    <FormattedMessage id={id} />
  </p>
);

const Callout = ({ id, email }) => (
  <div className={css.callout}>
    <p className={css.calloutText}>
      <FormattedMessage id={id} />
      {email ? (
        <>
          {' '}
          <a className={css.emailLink} href={`mailto:${email}`}>
            {email}
          </a>
        </>
      ) : null}
    </p>
  </div>
);

const Block = ({ block }) => {
  switch (block.type) {
    case 'p':
      return <Paragraph id={block.id} />;
    case 'callout':
      return <Callout id={block.id} email={block.email} />;
    default:
      return null;
  }
};

const Section = ({ section }) => {
  const content = (
    <>
      {section.titleId ? (
        <h2 className={css.sectionHeading}>
          {section.number ? `${section.number}. ` : null}
          <FormattedMessage id={section.titleId} />
        </h2>
      ) : null}
      {section.blocks.map((block, index) => (
        <Block block={block} key={index} />
      ))}
    </>
  );

  return (
    <div className={css.section}>
      {section.gradient ? (
        <div className={css.gradientBlock}>{content}</div>
      ) : section.accentBar ? (
        <div className={css.accentBarBlock}>{content}</div>
      ) : (
        content
      )}
    </div>
  );
};

/**
 * Content-only rendering of the Community Guidelines page, meant for
 * embedding elsewhere on the site.
 */
export const CommunityGuidelinesContent = () => (
  <div className={css.card}>
    {guidelineSections.map((section, index) => (
      <Section section={section} key={index} />
    ))}
  </div>
);

export const CommunityGuidelinesPageComponent = props => {
  const { scrollingDisabled } = props;
  const history = useHistory();
  const intl = useIntl();

  const handleDownloadPdf = () => {
    if (typeof window !== 'undefined') {
      window.print();
    }
  };

  const pageTitle = intl.formatMessage({ id: 'CommunityGuidelinesPage.pageTitle' });

  return (
    <Page title={pageTitle} scrollingDisabled={scrollingDisabled}>
      <LayoutSingleColumn topbar={<TopbarContainer />} footer={<FooterContainer />}>
        <div className={css.root}>
          <div className={css.heroSection}>
            <div className={css.topRow}>
              <button className={css.backLink} onClick={() => history.goBack()} type="button">
                <FormattedMessage id="CommunityGuidelinesPage.backBtn" />
              </button>
              <button className={css.downloadButton} onClick={handleDownloadPdf} type="button">
                <DownloadIcon className={css.icon} />
                <FormattedMessage id="CommunityGuidelinesPage.downloadBtn" />
              </button>
            </div>

            <h1 className={css.title}>{pageTitle}</h1>
            <div className={css.subtitleRow}>
              <span>{companyInfo.name}</span>
              <span className={css.subtitleDivider}>|</span>
              <span>
                <FormattedMessage id="CommunityGuidelinesPage.effectiveDate" />: {companyInfo.effectiveDate}
              </span>
            </div>
            <p className={css.intro}>
              <FormattedMessage id="CommunityGuidelinesPage.intro" />
            </p>
          </div>

          <div className={css.content}>
            <CommunityGuidelinesContent />
          </div>
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

const mapStateToProps = state => ({
  scrollingDisabled: isScrollingDisabled(state),
});

const CommunityGuidelinesPage = connect(mapStateToProps)(CommunityGuidelinesPageComponent);

export default CommunityGuidelinesPage;
